import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Copy,
  Gift,
  Loader2,
  Mic2,
  Send,
  Share2,
  Sparkles,
  UserRoundCheck,
} from "lucide-react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import SignedInSettingsShell from "@/components/shared/SignedInSettingsShell";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { useRouteScrollRestoration } from "@/hooks/use-route-scroll-restoration";
import {
  getCurrentAccountId,
  getReferralsOverview,
  setCurrentAccountId,
  trackEvent,
} from "@/lib/api";
import {
  redirectToAuthIfRequired,
  redirectToAuthIfSignedOut,
} from "@/lib/auth-required-redirect";
import { useI18n } from "@/lib/i18n";
import { EXISTING_PREFERENCES_PATH } from "@/lib/preferences-route";
import { getPlanLabel } from "@/lib/plans";
import { createReferralInviteLink } from "@/lib/referral-invite-links";
import { formatSettingsPlanSummary } from "@/lib/settings-shell";
import ReferralRewardBalances from "@/components/account/ReferralRewardBalances";

function formatDate(value) {
  const timestamp = Number(value || 0);
  if (!timestamp) return "";
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(timestamp * 1000));
}

function LoadingBlock({ className = "h-20" }) {
  return <div aria-hidden="true" className={`animate-pulse rounded-3xl bg-muted/70 ${className}`} />;
}

function ReferralStep({ icon: Icon, title, copy }) {
  return (
    <div className="flex gap-3 rounded-3xl border border-primary/10 bg-white/80 p-4 shadow-sm">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <h2 className="text-sm font-bold text-foreground">{title}</h2>
        <p className="mt-1 text-sm leading-6 text-muted-foreground">{copy}</p>
      </div>
    </div>
  );
}

export default function Referrals() {
  const { tx } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [initialStoredAccountId] = useState(() => getCurrentAccountId() || "");
  const requestedAccountId = searchParams.get("accountId") || initialStoredAccountId;
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [inviteActionState, setInviteActionState] = useState("idle");
  const [latestInviteUrl, setLatestInviteUrl] = useState("");
  const loadedTelemetryKey = useRef("");
  const inviteStateTimer = useRef(null);
  const activeInviteAccountId = useRef("");
  const inviteRequest = useRef(null);

  useRouteScrollRestoration();

  useEffect(() => () => {
    inviteRequest.current?.abort();
    if (inviteStateTimer.current) window.clearTimeout(inviteStateTimer.current);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadOverview() {
      try {
        if (await redirectToAuthIfSignedOut({ navigate, location })) return;
        inviteRequest.current?.abort();
        inviteRequest.current = null;
        setLoading(true);
        setLoadFailed(false);
        setLatestInviteUrl("");
        setInviteActionState("idle");
        const payload = await getReferralsOverview(requestedAccountId);
        if (cancelled) return;
        setOverview(payload);
        const accountId = String(payload?.accountId || "").trim();
        if (accountId) setCurrentAccountId(accountId);
      } catch (error) {
        if (cancelled) return;
        if (redirectToAuthIfRequired(error, { navigate, location })) return;
        setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadOverview();
    return () => {
      cancelled = true;
    };
  }, [location, navigate, requestedAccountId]);

  const accountId = String(overview?.accountId || requestedAccountId || "").trim();
  activeInviteAccountId.current = accountId;
  const accounts = Array.isArray(overview?.accounts) ? overview.accounts : [];
  const currentPlan = overview?.currentPlan || "";
  const rewards = overview?.rewards || null;
  const referrals = Array.isArray(overview?.referrals) ? overview.referrals : [];
  const referralsTotal = Number(overview?.referralsTotal ?? referrals.length);
  const inviteLinksEnabled = Boolean(
    overview?.sharing?.inviteLinksEnabled
    ?? overview?.sharing?.genericUrl,
  );
  const voiceLinksEnabled = overview?.sharing?.voiceLinksEnabled !== false;
  const rewardsEndDate = formatDate(rewards?.endsAt);
  const afterPlan = String(rewards?.afterRewards?.plan || "free").toLowerCase();
  const paidCoverageContinues = Boolean(rewards?.afterRewards?.paidCoverageContinues);
  const showKeepFun = Boolean(rewards?.totalMonths) && !paidCoverageContinues && afterPlan === "free";
  const billingRoute = accountId
    ? `/billing?accountId=${encodeURIComponent(accountId)}&plan=fun&source=referrals_expiry`
    : "/billing?plan=fun&source=referrals_expiry";
  const preferencesRoute = accountId
    ? `${EXISTING_PREFERENCES_PATH}&accountId=${encodeURIComponent(accountId)}`
    : EXISTING_PREFERENCES_PATH;
  const accountRoute = accountId
    ? `/account?accountId=${encodeURIComponent(accountId)}`
    : "/account";

  const planLabel = currentPlan ? `${getPlanLabel(currentPlan)} plan` : "Plan";
  const activeAccount = useMemo(
    () => accounts.find((account) => account.id === accountId) || accounts[0] || null,
    [accountId, accounts],
  );

  useEffect(() => {
    if (!overview) return;
    const telemetryKey = [
      overview.accountId,
      overview.currentPlan,
      overview.rewards?.totalMonths,
      overview.referralsTotal ?? overview.referrals?.length,
    ].join(":");
    if (loadedTelemetryKey.current === telemetryKey) return;
    loadedTelemetryKey.current = telemetryKey;
    trackEvent("referrals_overview_loaded", {
      current_plan: overview.currentPlan || "free",
      total_free_months: overview.rewards?.totalMonths || 0,
      completed_referrals: overview.referralsTotal ?? overview.referrals?.length ?? 0,
      app_duration_ms: overview.timing?.appDurationMs || 0,
      auth_duration_ms: overview.timing?.authDurationMs || 0,
      db_duration_ms: overview.timing?.dbDurationMs || 0,
      pool_wait_duration_ms: overview.timing?.poolWaitDurationMs || 0,
      db_query_count: overview.timing?.dbQueryCount || 0,
    });
  }, [overview]);

  function settleInviteState(nextState) {
    if (inviteStateTimer.current) window.clearTimeout(inviteStateTimer.current);
    setInviteActionState(nextState);
    inviteStateTimer.current = window.setTimeout(() => setInviteActionState("idle"), 1800);
  }

  async function createFreshInvite(action) {
    if (!accountId || !inviteLinksEnabled || inviteActionState === "creating") return false;
    const requestAccountId = accountId;
    const controller = new AbortController();
    inviteRequest.current?.abort();
    inviteRequest.current = controller;
    if (inviteStateTimer.current) window.clearTimeout(inviteStateTimer.current);
    setInviteActionState("creating");
    try {
      const nativeShare = action === "share" && navigator.share;
      if (!nativeShare && (!navigator.clipboard?.write || typeof ClipboardItem === "undefined")) {
        throw new Error("Clipboard is unavailable.");
      }
      const inviteUrlPromise = createReferralInviteLink(requestAccountId, {
        signal: controller.signal,
      }).then((payload) => {
        if (controller.signal.aborted || activeInviteAccountId.current !== requestAccountId) {
          throw new DOMException("Invite request cancelled.", "AbortError");
        }
        const url = String(payload?.url || "").trim();
        if (!url) throw new Error("Referral invite URL is missing.");
        setLatestInviteUrl(url);
        return url;
      });
      // Begin clipboard permission inside the click gesture, before awaiting the server.
      const clipboardWrite = nativeShare ? null : navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": inviteUrlPromise.then((url) => new Blob([url], { type: "text/plain" })),
        }),
      ]);
      const [inviteUrl] = await Promise.all([inviteUrlPromise, clipboardWrite]);
      if (controller.signal.aborted || activeInviteAccountId.current !== requestAccountId) return false;
      if (nativeShare) {
        try {
          await navigator.share({
            title: tx("Min makes WhatsApp voice notes easier"),
            text: tx("Min turns WhatsApp voice notes into quick summaries right inside WhatsApp."),
            url: inviteUrl,
          });
        } catch (error) {
          if (error?.name === "AbortError") {
            setInviteActionState("idle");
            return false;
          }
          throw error;
        }
        trackEvent("generic_referral_link_shared", { current_plan: currentPlan || "free" });
        settleInviteState("shared");
        return true;
      }
      trackEvent(
        action === "share" ? "generic_referral_link_shared" : "generic_referral_link_copied",
        { current_plan: currentPlan || "free" },
      );
      settleInviteState("copied");
      return true;
    } catch {
      if (
        controller.signal.aborted
        || activeInviteAccountId.current !== requestAccountId
      ) return false;
      controller.abort();
      setInviteActionState("error");
      return false;
    } finally {
      if (inviteRequest.current === controller) inviteRequest.current = null;
    }
  }

  async function shareGenericLink() {
    await createFreshInvite("share");
  }

  async function copyGenericLink() {
    await createFreshInvite("copy");
  }

  return (
    <SignedInSettingsShell
      currentSection="referrals"
      title="Referrals"
      onBack={() => navigate(-1)}
      planLabel={planLabel}
      planTooltipCopy={currentPlan ? formatSettingsPlanSummary(currentPlan, null) : ""}
      planTooltipLoading={loading}
      sectionLinks={{ preferences: preferencesRoute, account: accountRoute, billing: billingRoute }}
      headerNavAccessory={!loading && accounts.length > 1 ? (
        <Select
          value={accountId}
          onValueChange={(nextAccountId) => {
            if (!nextAccountId || nextAccountId === accountId) return;
            activeInviteAccountId.current = nextAccountId;
            inviteRequest.current?.abort();
            setCurrentAccountId(nextAccountId);
            navigate(`/referrals?accountId=${encodeURIComponent(nextAccountId)}`, { replace: true });
          }}
        >
          <SelectTrigger
            aria-label={tx("WhatsApp number")}
            className="h-9 w-[10rem] rounded-full border-border/70 bg-card text-left text-sm sm:h-10 sm:w-[12rem]"
          >
            <span className="truncate">{activeAccount?.label || tx("Linked number")}</span>
          </SelectTrigger>
          <SelectContent align="end">
            {accounts.map((account) => (
              <SelectItem key={account.id} value={account.id} className="py-2 pl-3 pr-8">
                <span className="truncate">{account.label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : null}
    >
      <section className="overflow-hidden rounded-[2rem] border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-sky-50 px-5 py-6 sm:px-7 sm:py-8">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary">{tx("Referral bonus")}</p>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight text-foreground sm:text-5xl">
            {tx("Send a voice note. You both get an extra week.")}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-7 text-muted-foreground">
            {tx("When your friend links WhatsApp, you both get one extra week of Fun. When they first pay, you earn a month of their plan.")}
          </p>
        </div>
        <div className="mt-6 grid gap-3 lg:grid-cols-3">
          <ReferralStep icon={Mic2} title={tx("Send a voice note")} copy={tx("Send your friend a normal WhatsApp voice note.")} />
          <ReferralStep icon={Sparkles} title={tx("Min makes their link")} copy={tx("Min adds their personal link to the result.")} />
          <ReferralStep icon={Gift} title={tx("You both get a week")} copy={tx("Your friend starts with a free month of Fun, plus your referral week.")} />
        </div>
        {!loading && !voiceLinksEnabled ? (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-950">
            {tx("Personal friend links are off because Min branding is off for this number.")} {" "}
            <Link to={preferencesRoute} className="font-bold underline underline-offset-2">{tx("Turn branding on")}</Link>
          </div>
        ) : null}
      </section>

      <section className="rounded-[1.75rem] border border-border bg-card p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-secondary text-primary">
            <Share2 className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{tx("Share another way")}</p>
            <h2 className="mt-1 font-display text-2xl font-bold text-foreground">{tx("Create a fresh referral link")}</h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{tx("Each link works for as many people as you like. Copy or share to make a fresh one.")}</p>
          </div>
        </div>
        {loading ? (
          <LoadingBlock className="mt-5 h-24" />
        ) : inviteLinksEnabled ? (
          <div className="mt-5 flex flex-col gap-3 rounded-3xl bg-muted/40 p-3 sm:flex-row sm:flex-wrap sm:items-center">
            <p className="ph-no-capture ph-mask min-w-0 flex-1 truncate px-2 font-mono text-sm text-foreground">
              {latestInviteUrl || tx("Make one when you're ready to share.")}
            </p>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <Button type="button" variant="outline" className="rounded-2xl" disabled={inviteActionState === "creating"} onClick={() => void copyGenericLink()}>
                {inviteActionState === "creating" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : inviteActionState === "copied" ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                {inviteActionState === "creating" ? tx("Creating link…") : inviteActionState === "copied" ? tx("Copied") : tx("Copy link")}
              </Button>
              <Button type="button" className="rounded-2xl" disabled={inviteActionState === "creating"} onClick={() => void shareGenericLink()}>
                <Send className="mr-2 h-4 w-4" />
                {tx("Share")}
              </Button>
            </div>
            {inviteActionState === "error" ? (
              <p className="text-sm font-medium text-destructive sm:basis-full">{tx("We couldn't create a referral link. Try again.")}</p>
            ) : null}
          </div>
        ) : (
          <p className="mt-5 rounded-2xl bg-muted/40 px-4 py-4 text-sm text-muted-foreground">{tx("Your referral link will appear here once this WhatsApp number is active.")}</p>
        )}
      </section>

      {loadFailed ? (
        <section className="rounded-3xl border border-destructive/20 bg-destructive/5 px-5 py-5">
          <p className="font-semibold text-foreground">{tx("We couldn't load your referral details.")}</p>
          <Button type="button" variant="outline" className="mt-4 rounded-2xl" onClick={() => window.location.reload()}>{tx("Try again")}</Button>
        </section>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[1.05fr_0.95fr]">
        <section className="rounded-[1.75rem] border border-border bg-card p-5 sm:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{tx("Free time earned")}</p>
          {loading ? (
            <div className="mt-4 space-y-3"><LoadingBlock className="h-16" /><LoadingBlock className="h-28" /><LoadingBlock className="h-24" /></div>
          ) : rewards ? (
            <>
              <ReferralRewardBalances rewards={rewards} />

              <div className="mt-5 rounded-3xl bg-[#0f172a] p-5 text-white">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-300">Fun</p>
                <ul className="mt-3 grid gap-2 text-sm text-slate-200 sm:grid-cols-2">
                  {["Incoming voice-note summaries", "Full transcripts", "Approval controls", "Summaries in another language"].map((benefit) => (
                    <li key={benefit} className="flex items-center gap-2"><Check className="h-4 w-4 text-sky-300" />{tx(benefit)}</li>
                  ))}
                </ul>
              </div>

              {paidCoverageContinues ? (
                <div className="mt-5 rounded-3xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-950">
                  {getPlanLabel(afterPlan)} · {tx("Keep current plan")}
                </div>
              ) : rewardsEndDate ? (
                <div className="mt-5 rounded-3xl border border-border p-4">
                  <p className="font-bold text-foreground">{rewardsEndDate}</p>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{getPlanLabel(afterPlan)}</p>
                  {showKeepFun ? (
                    <Button asChild className="mt-4 h-11 rounded-2xl px-5">
                      <Link
                        to={billingRoute}
                        onClick={() => trackEvent("referrals_keep_fun_clicked", { current_plan: currentPlan || "free" })}
                      >
                        {tx("Manage billing")}
                      </Link>
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </section>

        <section className="rounded-[1.75rem] border border-border bg-card p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{tx("Referral signups")}</p>
              <h2 className="mt-2 font-display text-2xl font-bold text-foreground">{tx("Friends you've referred")}</h2>
            </div>
            {!loading ? <span className="rounded-full bg-secondary px-3 py-1 text-sm font-bold text-primary">{referralsTotal}</span> : null}
          </div>
          {loading ? (
            <div className="mt-5 space-y-3"><LoadingBlock className="h-20" /><LoadingBlock className="h-20" /></div>
          ) : referrals.length ? (
            <div className="mt-5 space-y-3">
              {referrals.map((referral) => (
                <article key={referral.id} className="flex items-center gap-3 rounded-3xl border border-border/80 bg-muted/20 p-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary"><UserRoundCheck className="h-5 w-5" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-foreground">{referral.contactName}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{formatDate(referral.completedAt)}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800">+{tx("{days} days").replaceAll("{days}", String(referral.daysEarned ?? Number(referral.monthsEarned || 0) * 31))}</span>
                </article>
              ))}
              {referralsTotal > referrals.length ? (
                <p className="px-2 pt-1 text-center text-sm text-muted-foreground">
                  {tx("Showing your most recent referral signups.")}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="mt-5 rounded-3xl border border-dashed border-border px-5 py-10 text-center">
              <UserRoundCheck className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-3 font-bold text-foreground">{tx("No referral signups yet. Your next completed signup will show up here.")}</p>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{tx("New signups from your links will appear here.")}</p>
            </div>
          )}
        </section>
      </div>
    </SignedInSettingsShell>
  );
}
